/**
 * Internal Observability API Service
 * 
 * Provides methods for:
 * - Version info and release notes
 * - Feedback submission
 * - Context capture for bug reports
 */

import api from './api';

const observabilityAPI = {
  // ==========================================================================
  // Version & Release Notes
  // ==========================================================================
  
  /**
   * Get current version info and internal user status
   * @returns {Promise<Object>} Version info with is_internal_user flag
   */
  async getVersionInfo() {
    const response = await api.get('/internal/version/');
    return response.data;
  },
  
  /**
   * Mark current version as seen (hide What's New indicator)
   * @returns {Promise<Object>} Confirmation with marked version
   */
  async markVersionSeen() {
    const response = await api.post('/internal/version/seen/');
    return response.data;
  },
  
  /**
   * Get release notes for internal users
   * @param {string} sinceVersion - Optional: only get notes newer than this version
   * @returns {Promise<Object>} Release notes array
   */
  async getReleaseNotes(sinceVersion = null) {
    const params = sinceVersion ? { since_version: sinceVersion } : {};
    const response = await api.get('/internal/release-notes/', { params });
    return response.data;
  },
  
  // ==========================================================================
  // Feedback Submission
  // ==========================================================================
  
  /**
   * Submit internal feedback (bug, feature request, etc.)
   * @param {Object} feedback - Feedback data
   * @param {string} feedback.feedback_type - 'bug', 'feature', 'feedback', 'question'
   * @param {string} feedback.title - Short summary
   * @param {string} feedback.description - Detailed description
   * @param {string} feedback.priority - 'low', 'medium', 'high', 'critical'
   * @param {string} [feedback.steps_to_reproduce] - For bugs
   * @param {string} [feedback.expected_behavior] - For bugs
   * @param {string} [feedback.actual_behavior] - For bugs
   * @returns {Promise<Object>} Created feedback object
   */
  async submitFeedback(feedback) {
    // Auto-capture context
    const context = this.captureContext();

    // Bridge to Sentry: attach the most recent Sentry event ID so the feedback
    // item can be correlated with the error in the Sentry dashboard.
    const sentryEventId = this._getLastSentryEventId();
    const errorTrackingData = sentryEventId
      ? { error_tracking_event_id: sentryEventId }
      : {};

    const response = await api.post('/internal/feedback/', {
      ...feedback,
      ...context,
      ...errorTrackingData,
    });
    return response.data;
  },
  
  /**
   * Get list of feedback items (internal/admin only)
   * @param {Object} filters - Optional filters
   * @param {string} filters.application - 'adi', 'lms', 'couples', 'platform'
   * @param {string} filters.status - 'new', 'acknowledged', 'in_progress', 'resolved', 'finished', 'wont_fix'
   * @param {string} filters.type - 'bug', 'feature', 'feedback', 'question'
   * @returns {Promise<Array>} List of feedback items
   */
  async getFeedbackList(filters = {}) {
    const response = await api.get('/internal/feedback/', { params: filters });
    return response.data;
  },

  /**
   * Alias used by IdeaRoadmap — returns items visible to the current user.
   * Pass { roadmap: true } to indicate the caller only wants public-stage items.
   * @param {object} filters
   */
  async getFeedback(filters = {}) {
    const response = await api.get('/internal/feedback/', { params: filters });
    return response.data;
  },
  
  /**
   * Get single feedback item with comments
   * @param {number} id - Feedback ID
   * @returns {Promise<Object>} Full feedback object
   */
  async getFeedbackDetail(id) {
    const response = await api.get(`/internal/feedback/${id}/`);
    return response.data;
  },

  /**
   * Upload a single file attachment to a feedback card.
   * @param {number} feedbackId - Feedback card ID
   * @param {File} file - File object to upload
   * @returns {Promise<Object>} Created attachment { id, file_name, mime_type, url, created_at }
   */
  async uploadAttachment(feedbackId, file) {
    const form = new FormData();
    form.append('file', file);
    // Do NOT manually set Content-Type — Axios must set it with the multipart boundary
    const response = await api.post(
      `/internal/feedback/${feedbackId}/add_attachment/`,
      form
    );
    return response.data;
  },

  /**
   * Delete a file attachment from a feedback card.
   * @param {number} feedbackId - Feedback card ID
   * @param {number} attachmentId - Attachment ID to delete
   */
  async deleteAttachment(feedbackId, attachmentId) {
    await api.delete(`/internal/feedback/${feedbackId}/delete_attachment/${attachmentId}/`);
  },

  /**
   * Add comment to feedback
   * @param {number} feedbackId - Feedback ID
   * @param {string} comment - Comment text
   * @param {string} [newStatus] - Optional status change
   * @returns {Promise<Object>} Created comment
   */
  async addFeedbackComment(feedbackId, comment, newStatus = null) {
    const data = { comment };
    if (newStatus) data.new_status = newStatus;
    
    const response = await api.post(`/internal/feedback/${feedbackId}/add_comment/`, data);
    return response.data;
  },
  
  /**
   * Resolve feedback
   * @param {number} feedbackId - Feedback ID
   * @param {string} [notes] - Resolution notes
   * @returns {Promise<Object>} Updated feedback
   */
  async resolveFeedback(feedbackId, notes = '') {
    const response = await api.post(`/internal/feedback/${feedbackId}/resolve/`, {
      resolution_notes: notes
    });
    return response.data;
  },
  
  /**
   * Get feedback statistics (internal/admin only)
   * @returns {Promise<Object>} Stats by status, application, type
   */
  async getFeedbackStats() {
    const response = await api.get('/internal/feedback/stats/');
    return response.data;
  },

  /**
   * Update the status of a feedback item inline (used by status dropdown on cards)
   * @param {number} feedbackId - Feedback ID
   * @param {string} status - New status value
   * @returns {Promise<Object>} Updated feedback
   */
  async updateFeedbackStatus(feedbackId, status) {
    const response = await api.patch(`/internal/feedback/${feedbackId}/`, { status });
    return response.data;
  },

  /**
   * AI quick-capture → one or more structured draft cards.
   *
   * Takes a raw brain-dump string (which may contain multiple ideas separated
   * by bullets, numbers, blank lines, headings, etc.) and returns a list of
   * fully populated draft objects including Lovie's Take scores — without
   * saving anything to the database.
   *
   * The AI detects natural delimiters and splits the input into as many
   * distinct cards as it finds (capped at 20). A single-idea input returns
   * an array with one element.
   *
   * @param {string} quickCapture - Raw idea text (may contain multiple ideas)
   * @param {string} [feedbackType] - Optional type hint: 'bug'|'feature'|'feedback'|'question'
   * @returns {Promise<{drafts: Object[], memory_used: boolean}>}
   */
  async draftFeedback(quickCapture, feedbackType = '') {
    const response = await api.post('/internal/feedback/draft/', {
      quick_capture: quickCapture,
      feedback_type: feedbackType,
    });
    return response.data;
  },

  /**
   * Trigger CURie's Take assessment for a feedback item.
   * Generates difficulty, feasibility, impact scores and a brief implementation plan.
   * @param {number} feedbackId - Feedback ID
   * @returns {Promise<Object>} Updated feedback with ai_difficulty, ai_cost, ai_impact, ai_plan
   */
  async assessFeedback(feedbackId) {
    const response = await api.post(`/internal/feedback/${feedbackId}/assess/`);
    return response.data;
  },

  /**
   * Generate a CURie test plan for a feedback item.
   * Returns step-by-step test cases stored in ai_test_plan (JSON array).
   * @param {number} feedbackId - Feedback ID
   * @returns {Promise<Object>} Updated feedback with ai_test_plan, ai_test_plan_at
   */
  async generateTestPlan(feedbackId) {
    const response = await api.post(`/internal/feedback/${feedbackId}/generate_test_plan/`);
    return response.data;
  },

  /**
   * Generate a persisted implementation plan for a feedback item.
   * Also moves the item to acknowledged status.
   * @param {number} feedbackId - Feedback ID
   * @returns {Promise<{plan: string, feedback: Object}>}
   */
  async generateChecklist(feedbackId) {
    const response = await api.post(`/internal/feedback/${feedbackId}/generate_checklist/`);
    return response.data;
  },

  /**
   * Generate steps_to_reproduce when empty.
   * @param {number} feedbackId
   * @returns {Promise<Object>} Updated feedback
   */
  async generateReproSteps(feedbackId) {
    const response = await api.post(`/internal/feedback/${feedbackId}/generate_repro_steps/`);
    return response.data;
  },

  /**
   * Estimate implementation + testing hours for a card.
   */
  async estimateFeedback(feedbackId) {
    const response = await api.post(`/internal/feedback/${feedbackId}/estimate/`);
    return response.data;
  },

  /**
   * Start bulk queue automation (Celery). Returns { task_id, action }.
   */
  async startBulkQueue(action) {
    const response = await api.post('/internal/feedback/bulk-queue/', { action });
    return response.data;
  },

  /**
   * Poll bulk queue task progress.
   */
  async getBulkQueueStatus(taskId) {
    const response = await api.get('/internal/feedback/bulk-queue-status/', {
      params: { task_id: taskId },
    });
    return response.data;
  },

  /**
   * Get schedule timeline for assigned cards.
   */
  async getSchedule(assigneeId = null) {
    const params = assigneeId ? { assignee_id: assigneeId } : {};
    const response = await api.get('/internal/feedback/schedule/', { params });
    return response.data;
  },

  /**
   * Update the priority of a feedback item.
   * @param {number} feedbackId - Feedback ID
   * @param {string} priority - New priority (activate|commit|consider|explore|shelve|skip)
   * @returns {Promise<Object>} Updated feedback
   */
  async updateFeedbackPriority(feedbackId, priority) {
    const response = await api.patch(`/internal/feedback/${feedbackId}/`, { priority });
    return response.data;
  },

  /**
   * Update the feature area (module) of a feedback item.
   * @param {number} feedbackId - Feedback ID
   * @param {string} featureArea - Module name, or '' to clear
   * @returns {Promise<Object>} Updated feedback
   */
  async updateFeedbackModule(feedbackId, featureArea) {
    const response = await api.patch(`/internal/feedback/${feedbackId}/`, { feature_area: featureArea });
    return response.data;
  },

  async listCategories() {
    const response = await api.get('/internal/feedback/categories/');
    return response.data;
  },

  async createCategory(payload) {
    const response = await api.post('/internal/feedback/categories/', payload);
    return response.data;
  },

  async updateCategory(id, payload) {
    const response = await api.patch(`/internal/feedback/categories/${id}/`, payload);
    return response.data;
  },

  async patchFeedback(feedbackId, data) {
    const response = await api.patch(`/internal/feedback/${feedbackId}/`, data);
    return response.data;
  },

  /**
   * Paginated queue list for IWI Queue Manager grid.
   * @param {Object} params - page, page_size, search, status, priority, assigned_to, etc.
   * @returns {Promise<{count, next, previous, results}>}
   */
  async getQueueList(params = {}) {
    const response = await api.get('/internal/feedback/queue/', { params });
    return response.data;
  },

  /**
   * Batch workflow edits from Queue Manager.
   * @param {Object} payload - { updates: [...] } or { ids, patch, append_plan_notes? }
   * @returns {Promise<{updated: number, errors: Array}>}
   */
  async bulkUpdateFeedback(payload) {
    const response = await api.post('/internal/feedback/bulk-update/', payload);
    return response.data;
  },

  async getPilotSyncConfig() {
    const response = await api.get('/internal/feedback/pilot-sync-config/');
    return response.data;
  },

  async syncFeedbackToPilot(ids) {
    const response = await api.post('/internal/feedback/sync-to-pilot/', { ids });
    return response.data;
  },

  /**
   * Ask Pollie to suggest the best-matching module for a card.
   * Does NOT auto-save — returns { suggested_module: string }.
   * @param {number} feedbackId - Feedback ID
   * @returns {Promise<{suggested_module: string}>}
   */
  async suggestModule(feedbackId) {
    const response = await api.post(`/internal/feedback/${feedbackId}/suggest_module/`);
    return response.data;
  },

  /**
   * Return the list of team members who can be assigned to IWI cards.
   * @returns {Promise<Array<{id: number, email: string, name: string}>>}
   */
  async getAssignableUsers() {
    const response = await api.get('/internal/feedback/assignable-users/');
    return response.data;
  },

  /**
   * Chat with Pollie to generate a client-facing summary for a card.
   * Pollie reads the card context and either returns a suggested client_summary
   * or asks a clarifying question if the description is ambiguous.
   * Does NOT auto-save — frontend shows suggested_summary and lets admin accept.
   * @param {number} feedbackId - Feedback ID
   * @param {string} message - Admin message ('generate' for first turn, or a reply)
   * @param {Array<{role: string, content: string}>} history - Conversation history
   * @returns {Promise<{reply: string, suggested_summary: string|null, confidence: number}>}
   */
  async pollieChat(feedbackId, message = 'generate', history = []) {
    const response = await api.post(`/internal/feedback/${feedbackId}/pollie_chat/`, {
      message,
      history,
    });
    return response.data;
  },

  /**
   * Ask Pollie to suggest a target delivery month for a card.
   * Pollie looks at queue position, difficulty, and priority of other active cards.
   * Does NOT auto-save — frontend shows the suggestion and lets admin accept or override.
   * @param {number} feedbackId - Feedback ID
   * @returns {Promise<{suggested_month: string, display: string, reasoning: string}>}
   */
  async suggestTargetMonth(feedbackId) {
    const response = await api.post(`/internal/feedback/${feedbackId}/suggest_target_month/`);
    return response.data;
  },

  /**
   * Upload a file (Word, Excel, CSV, PDF, TXT) and get an AI-structured draft card back.
   * Returns the same { draft, memory_used } shape as draftFeedback().
   * @param {File} file - The file to ingest
   * @returns {Promise<{draft: Object, memory_used: boolean}>}
   */
  async ingestFile(file) {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/internal/feedback/ingest_file/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Bulk import feedback items from an AccessIQ export or Lovable JSON export.
   * AccessIQ exports include sync_uuid and are upserted (create or update).
   * Lovable exports fall back to skip-by-title deduplication.
   * @param {Array} items - Array of item objects
   * @returns {Promise<Object>} { created, updated, skipped, errors, message }
   */
  async importFeedback(items) {
    const response = await api.post('/internal/feedback/import_feedback/', { items });
    return response.data;
  },

  /**
   * Export all board cards as a downloadable JSON file.
   * The file includes sync_uuid so importing it into another environment
   * upserts existing cards rather than creating duplicates.
   * Triggers a browser file download automatically.
   * @returns {Promise<void>}
   */
  async exportFeedback() {
    const response = await api.get('/internal/feedback/export/', {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `iwi-export-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
  
  // ==========================================================================
  // Context Capture
  // ==========================================================================
  
  /**
   * Capture current page context for feedback submission.
   * Includes URL, route, browser info, screen size, and recent errors.
   * @returns {Object} Context data
   */
  captureContext() {
    const context = {
      page_url: window.location.href,
      page_route: this._getCurrentRouteName(),
      browser_info: navigator.userAgent,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      console_errors: this._getRecentConsoleErrors(),
      network_failures: this._getRecentNetworkFailures(),
    };
    
    return context;
  },
  
  /**
   * Get current Vue route name if available
   * @private
   */
  _getCurrentRouteName() {
    // Try to access Vue Router if available
    if (window.__VUE_APP__ && window.__VUE_APP__.$route) {
      return window.__VUE_APP__.$route.name || '';
    }
    // Fallback: parse from URL
    const path = window.location.pathname;
    return path.split('/').filter(Boolean).join('-') || 'home';
  },
  
  /**
   * Get recent console errors (if captured)
   * @private
   */
  _getRecentConsoleErrors() {
    // Check if error capture is enabled
    if (window.__CAPTURED_ERRORS__) {
      return window.__CAPTURED_ERRORS__.slice(-5); // Last 5 errors
    }
    return [];
  },
  
  /**
   * Get recent network failures (if captured)
   * @private
   */
  _getRecentNetworkFailures() {
    // Check if network failure capture is enabled
    if (window.__NETWORK_FAILURES__) {
      return window.__NETWORK_FAILURES__.slice(-5); // Last 5 failures
    }
    return [];
  },
  
  /**
   * Retrieve the last Sentry event ID if Sentry is active.
   * Used to link a feedback submission to its corresponding Sentry error event.
   * @private
   */
  _getLastSentryEventId() {
    try {
      // @sentry/vue exposes lastEventId() globally after init
      if (typeof window !== 'undefined' && window.__SENTRY__) {
        return window.__SENTRY__.hub?.lastEventId?.() || null;
      }
      // Fallback: Sentry SDK may expose it directly
      if (typeof window !== 'undefined' && window.Sentry?.lastEventId) {
        return window.Sentry.lastEventId();
      }
    } catch (_) {
      // Sentry not available
    }
    return null;
  },

  // ==========================================================================
  // Error Capture Setup (call once at app init)
  // ==========================================================================
  
  /**
   * Initialize error capture for better bug reports.
   * Call this once when the app initializes.
   */
  initErrorCapture() {
    // Capture console errors
    window.__CAPTURED_ERRORS__ = [];
    const originalError = console.error;
    console.error = function(...args) {
      window.__CAPTURED_ERRORS__.push({
        timestamp: new Date().toISOString(),
        message: args.map(a => String(a)).join(' '),
      });
      // Keep only last 10
      if (window.__CAPTURED_ERRORS__.length > 10) {
        window.__CAPTURED_ERRORS__.shift();
      }
      originalError.apply(console, args);
    };
    
    // Capture network failures
    window.__NETWORK_FAILURES__ = [];
    
    // Intercept fetch failures
    const originalFetch = window.fetch;
    window.fetch = async function(...args) {
      try {
        const response = await originalFetch.apply(this, args);
        if (!response.ok) {
          window.__NETWORK_FAILURES__.push({
            timestamp: new Date().toISOString(),
            url: args[0],
            status: response.status,
            statusText: response.statusText,
          });
          if (window.__NETWORK_FAILURES__.length > 10) {
            window.__NETWORK_FAILURES__.shift();
          }
        }
        return response;
      } catch (error) {
        window.__NETWORK_FAILURES__.push({
          timestamp: new Date().toISOString(),
          url: args[0],
          error: error.message,
        });
        if (window.__NETWORK_FAILURES__.length > 10) {
          window.__NETWORK_FAILURES__.shift();
        }
        throw error;
      }
    };
  },
  /**
   * Fetch IWI commit tickets for What's New — recent Launched tickets with origin=Commit.
   * @param {string} createdAfter  - ISO date string (YYYY-MM-DD)
   * @param {string} createdBefore - ISO date string (YYYY-MM-DD), optional
   * @returns {Promise<Array>} Array of InternalFeedback objects
   */
  async getWhatsNewTickets(createdAfter, createdBefore = null) {
    const params = { status: 'finished', origin: 'Commit', created_after: createdAfter };
    if (createdBefore) params.created_before = createdBefore;
    const response = await api.get('/internal/feedback/', { params });
    const data = response.data;
    return data.results || data;
  },

  /**
   * Platform-admin-only: make internal IWI cards for commits that don't have one yet.
   * @param {number|null} hours - Hours of history; null = since the newest commit card (max 7 days)
   * @returns {Promise<{created: number, skipped: number, remaining: number, message: string}>}
   */
  async syncCommits(hours = null) {
    const response = await api.post('/internal/feedback/sync-commits/', hours ? { hours } : {});
    return response.data;
  },

  /**
   * Platform-admin-only: Pollie drafts hidden customer cards from new commit cards.
   * @returns {Promise<{checked: number, shared: number, drafts: number, remaining: number, message: string}>}
   */
  async draftCustomerCards() {
    const response = await api.post('/internal/feedback/draft-customer-cards/', {});
    return response.data;
  },

  /**
   * Generate a standup report and return the HTML for inline viewing.
   * Does NOT send any email.
   * @param {number} hours - Hours of commits to include (default 24)
   * @param {string} mode - 'commits' (default) or 'iwi'
   * @returns {Promise<{html: string, commit_count: number, date_label: string, since_hours: number, mode: string}>}
   */
  async previewStandup(hours = 24, mode = 'commits') {
    const response = await api.post('/internal/standup/preview/', { hours, mode });
    return response.data;
  },

  /**
   * Generate and send the standup email immediately.
   * @param {number} hours - Hours of commits to include (default 24)
   * @param {string} mode - 'commits' (default) or 'iwi'
   * @returns {Promise<{sent: boolean, commit_count: number, date_label: string}>}
   */
  async sendStandup(hours = 24, mode = 'commits') {
    const response = await api.post('/internal/standup/send/', { hours, mode });
    return response.data;
  },

  /**
   * Send an email to the requesting user listing IWI cards that are assigned
   * to someone else and have had no update in the past 24 hours.
   * @returns {Promise<{sent: boolean, count: number, message?: string}>}
   */
  async sendStaleReminder() {
    const response = await api.post('/internal/feedback/send_stale_reminder/');
    return response.data;
  },

  /**
   * Fetch the IWI OrgEngagement upload URL for the current user's org.
   * Returns { upload_url: string|null } — null if IWI is not enabled for the org.
   */
  async getMyUploadLinks() {
    const response = await api.get('/users/my-upload-links/');
    return response.data;
  },

  /**
   * Fetch the organizations the current (non-admin) user can pick as
   * "On behalf of" — their own org + any additional_organizations
   * (multi-org users like Matt: Utah + Colorado).
   * @returns {Promise<{organizations: Array<{id, name, display_name}>}>}
   */
  async getMySelectableOrganizations() {
    const response = await api.get('/users/my-selectable-organizations/');
    return response.data;
  },

  /**
   * Fetch the full client org catalog — used for "On behalf of" pickers in
   * admin-only surfaces (Queue Manager) and by platform admins in the create modal.
   * @returns {Promise<Array<{id, name, display_name}>>}
   */
  async getClientOrgCatalog() {
    const response = await api.get('/client-management/overview/');
    return response.data.organizations || [];
  },
};

export default observabilityAPI;
